import { Component, OnInit, ViewChild, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom, map } from 'rxjs';
import {
  Campaign,
  CampaignControllerService,
  CampaignExecutionCustomer,
  CampaignGift,
  CampaignHistorySummaryDTO,
  CampaignProductValidationDTO
} from 'src/app/admin-api';
import { TableColumn, TableComponent } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-campaign-history',
  templateUrl: './campaign-history.component.html',
  styleUrl: './campaign-history.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class CampaignHistoryComponent implements OnInit {
  @ViewChild('historyTable') historyTable?: TableComponent;

  private campaignService = inject(CampaignControllerService);
  private route = inject(ActivatedRoute);
  private title = inject(Title);

  private historyCustomersSignal = signal<CampaignExecutionCustomer[]>([]);
  private searchValue = signal<string>('');

  filterForm = new FormGroup({
    search: new FormControl<string>('')
  });

  campaignId: number | null = null;
  reportModelName = 'campanha-historico';
  campaign: Campaign | null = null;
  historySummary: CampaignHistorySummaryDTO | null = null;
  gifts: Array<CampaignGift & { validation?: CampaignProductValidationDTO }> = [];

  filteredCustomers = computed(() => {
    const term = this.searchValue().trim().toLowerCase();
    const all = this.historyCustomersSignal();
    if (!term) return all;
    return all.filter(
      (customer) =>
        customer.email?.toLowerCase().includes(term) ||
        String(customer.personId).includes(term) ||
        String(customer.shippingOrderId || '').includes(term)
    );
  });

  columns = [
    new TableColumn.Builder()
      .setHeader('ID')
      .setField('personId')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('E-mail')
      .setField('email')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('Método de Entrega')
      .setField('shipmentType')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('ShippingOrderId')
      .setField('shippingOrderId')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('resultStatus')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('Mensagem')
      .setField('resultMessage')
      .setFilter(false)
      .setType('text')
      .build()
  ];

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Marketing - Histórico da Campanha');
    const id = this.route.snapshot.paramMap.get('campaignId');
    if (id) {
      this.campaignId = Number(id);
      this.reportModelName = `campanha-${this.campaignId}-historico`;
      LoaderService.showLoader();
      try {
        await Promise.all([this.loadCampaign(), this.loadHistory(false)]);
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  updateSearch(): void {
    this.searchValue.set(this.filterForm.controls['search'].value ?? '');
  }

  async loadCampaign(): Promise<void> {
    if (!this.campaignId) return;
    try {
      this.campaign = await lastValueFrom(
        this.campaignService
          .getCampaign(this.campaignId)
          .pipe(map((r) => r.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
      throw error;
    }
  }

  async loadHistory(showLoader = true): Promise<void> {
    if (!this.campaignId) return;
    if (showLoader) LoaderService.showLoader();
    try {
      const gifts = await lastValueFrom(
        this.campaignService
          .listGifts(this.campaignId)
          .pipe(map((r) => r.result))
      );
      this.gifts = gifts.map((g) => ({ ...g, _validating: !!g.internalEan }));
      await Promise.all(
        gifts.map(async (gift, i) => {
          if (!gift.internalEan) return;
          try {
            const validation = await lastValueFrom(
              this.campaignService
                .validateProduct({ ean: gift.internalEan })
                .pipe(map((r) => r.result))
            );
            this.gifts = this.gifts.map((g, idx) =>
              idx === i ? { ...g, validation, _validating: false } : g
            );
          } catch (error) {
            this.gifts = this.gifts.map((g, idx) =>
              idx === i ? { ...g, _validating: false } : g
            );
          }
        })
      );
      this.historySummary = await lastValueFrom(
        this.campaignService
          .getHistorySummary(this.campaignId)
          .pipe(map((r) => r.result))
      );
      this.historyCustomersSignal.set(
        await lastValueFrom(
          this.campaignService
            .listHistoryCustomers(this.campaignId)
            .pipe(map((r) => r.result))
        )
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
      throw error;
    } finally {
      if (showLoader) LoaderService.showLoader(false);
    }
  }

  exportHistoryReport(): void {
    if (!this.historyTable || !this.filteredCustomers().length) return;
    this.historyTable.exportTable(true, 'xls');
  }
}
